<template>
  <BContainer
    fluid
    class="mb-4"
  >
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5
        class="fw-bold mb-0"
        style="color: #1e293b;"
      >
        My Feedback History
      </h5>
    </div>

    <BAlert
      v-if="linkState.error"
      show
      variant="warning"
      class="mb-3"
    >
      {{ linkState.error }}
    </BAlert>

    <BCard
      v-if="showEmailForm"
      class="border shadow-sm mb-3"
    >
      <BCardHeader class="bg-transparent border-bottom-0 pb-0">
        <h6 class="mb-1 fw-bold">
          Submit Appointment Feedback
        </h6>
        <small class="text-muted">Complete this form once for your concluded appointment.</small>
      </BCardHeader>
      <BCardBody>
        <BOverlay
          :show="state.loadingContext || state.submitting"
          opacity="0.4"
        >
          <BForm @submit.prevent="submitFeedback">
            <BRow>
              <BCol
                cols="12"
                md="6"
              >
                <BFormGroup
                  label="Appointment"
                  label-for="feedback-appointment"
                  label-class="font-weight-bold"
                  class="mb-2"
                >
                  <BFormSelect
                    id="feedback-appointment"
                    v-model="form.appointment_id"
                    :disabled="true"
                    :options="appointmentOptions"
                  />
                </BFormGroup>
              </BCol>

              <BCol
                cols="12"
                md="6"
              >
                <BFormGroup
                  label="Recipient"
                  label-for="feedback-recipient"
                  label-class="font-weight-bold"
                  class="mb-2"
                >
                  <BFormSelect
                    id="feedback-recipient"
                    v-model="form.recipient_id"
                    :disabled="state.submitting"
                    :options="recipientOptions"
                  />
                </BFormGroup>
              </BCol>

              <BCol
                cols="12"
                md="6"
              >
                <BFormGroup
                  label="Feedback Type"
                  label-for="feedback-type"
                  label-class="font-weight-bold"
                  class="mb-2"
                >
                  <BFormSelect
                    id="feedback-type"
                    v-model="form.feedback_type"
                    :disabled="state.submitting"
                    :options="feedbackTypeOptions"
                  />
                </BFormGroup>
              </BCol>

              <BCol
                v-if="form.feedback_type === 'other'"
                cols="12"
                md="6"
              >
                <BFormGroup
                  label="Specify Type"
                  label-for="feedback-other-type"
                  label-class="font-weight-bold"
                  class="mb-2"
                >
                  <BFormInput
                    id="feedback-other-type"
                    v-model.trim="form.other_type"
                    maxlength="255"
                    :disabled="state.submitting"
                  />
                </BFormGroup>
              </BCol>

              <BCol
                cols="12"
                md="6"
              >
                <BFormGroup
                  label="Rating"
                  label-for="feedback-rating"
                  label-class="font-weight-bold"
                  class="mb-2"
                >
                  <BFormSelect
                    id="feedback-rating"
                    v-model="form.rating"
                    :disabled="state.submitting"
                    :options="ratingOptions"
                  />
                </BFormGroup>
              </BCol>

              <BCol cols="12">
                <BFormGroup
                  label="Comments (Optional)"
                  label-for="feedback-comments"
                  label-class="font-weight-bold"
                  class="mb-2"
                >
                  <BFormTextarea
                    id="feedback-comments"
                    v-model.trim="form.comments"
                    rows="4"
                    maxlength="1000"
                    :disabled="state.submitting"
                    placeholder="Share your feedback"
                  />
                </BFormGroup>
              </BCol>
            </BRow>

            <div class="d-flex justify-content-end mt-3">
              <BButton
                type="submit"
                variant="primary"
                :disabled="state.submitting || !canSubmit"
              >
                <i :class="['fas me-1', state.submitting ? 'fa-spinner fa-spin' : 'fa-paper-plane']" />
                {{ state.submitting ? 'Submitting...' : 'Submit Feedback' }}
              </BButton>
            </div>
          </BForm>
        </BOverlay>
      </BCardBody>
    </BCard>

    <BCard class="border shadow-sm">
      <BCardText>
        <BOverlay
          :show="state.loading"
          opacity="0.5"
        >
          <BTable
            :items="feedbackItems"
            :fields="tableFields"
            responsive
            stacked="md"
            show-empty
            hover
            class="mb-0"
          >
            <template #empty>
              <div class="text-center py-4">
                <i
                  class="fas fa-comment-dots mb-2"
                  style="font-size: 2rem; color: #cbd5e1;"
                />
                <p
                  class="small mb-0"
                  style="color: #94a3b8;"
                >
                  No feedback records yet
                </p>
              </div>
            </template>

            <template #cell(rating)="{ value }">
              <span class="text-warning">
                <i
                  v-for="n in Number(value || 0)"
                  :key="n"
                  class="fas fa-star me-1"
                />
              </span>
              <span class="ms-1 small text-muted">({{ value }})</span>
            </template>

            <template #cell(date_submitted)="{ value }">
              {{ formatDate(value) }}
            </template>
          </BTable>
        </BOverlay>

        <div
          v-if="pagination.last_page > 1"
          class="d-flex justify-content-end mt-3"
        >
          <BPagination
            v-model="pagination.current_page"
            :total-rows="pagination.total"
            :per-page="pagination.per_page"
            size="sm"
            @change="onPageChange"
          />
        </div>
      </BCardText>
    </BCard>
  </BContainer>
</template>

<script>
import MxMisc from '@/mixins/misc'
import { SSharedList } from '@/services/shared'
import { SStudentFeedback } from '@/services/student'

const defaultForm = () => ({
  appointment_id: null,
  recipient_id: null,
  rating: 5,
  comments: '',
  feedback_type: 'appointment',
  other_type: '',
  link_token: null
})

export default {
  name: 'StudentFeedback',

  mixins: [MxMisc],

  middleware: ['auth', 'student'],

  metaInfo: () => ({ title: 'My Feedback' }),

  data () {
    return {
      form: defaultForm(),
      feedbackItems: [],
      recipients: [],
      linkAppointment: null,
      linkState: {
        error: ''
      },
      pagination: { current_page: 1, per_page: 10, total: 0, last_page: 1 },
      state: {
        loading: false,
        loadingContext: false,
        submitting: false
      }
    }
  },

  computed: {
    showEmailForm () {
      return this.$route?.query?.submit === '1' && Boolean(this.linkToken)
    },

    linkToken () {
      return typeof this.$route?.query?.token === 'string' ? this.$route.query.token : ''
    },

    feedbackTypeOptions () {
      return [
        { value: 'appointment', text: 'Appointment' },
        { value: 'service', text: 'Service' },
        { value: 'other', text: 'Other' }
      ]
    },

    ratingOptions () {
      return [1, 2, 3, 4, 5].map(v => ({ value: v, text: `${v} Star${v > 1 ? 's' : ''}` }))
    },

    appointmentOptions () {
      if (!this.linkAppointment) return []

      const concern = this.linkAppointment.appointment_concern_type?.name || 'Appointment'
      const slot = this.linkAppointment.appointment_slot
        ? `${this.linkAppointment.appointment_slot.date} ${this.linkAppointment.appointment_slot.start_time || ''}`
        : 'No slot details'

      return [{
        value: this.linkAppointment.id,
        text: `#${this.linkAppointment.id} - ${concern} (${slot.trim()})`
      }]
    },

    recipientOptions () {
      const fromAppointment = this.linkAppointment?.user
      if (fromAppointment?.id) {
        const text = `${fromAppointment.first_name || ''} ${fromAppointment.last_name || ''}`.trim() || fromAppointment.username || `User #${fromAppointment.id}`
        return [{ value: fromAppointment.id, text }]
      }

      return this.recipients.map(u => ({
        value: u.id,
        text: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.name || `User #${u.id}`
      }))
    },

    canSubmit () {
      if (!this.linkAppointment) return false
      if (!this.form.appointment_id || !this.form.rating || !this.form.feedback_type) return false
      if (this.form.feedback_type === 'other' && !this.form.other_type) return false
      return !!this.form.recipient_id || !!this.linkAppointment?.user_id
    },

    tableFields () {
      return [
        { key: 'appointment_id', label: 'Appointment' },
        { key: 'feedback_type', label: 'Type' },
        { key: 'rating', label: 'Rating' },
        { key: 'comments', label: 'Comments' },
        { key: 'date_submitted', label: 'Date Submitted' }
      ]
    }
  },

  async mounted () {
    await this.fetchFeedback()

    if (this.$route?.query?.submit === '1') {
      await this.initializeLinkForm()
    }
  },

  methods: {
    formatDate (value) {
      if (!value) return '-'
      return new Date(value).toLocaleString()
    },

    onPageChange (page) {
      this.pagination.current_page = page
      this.fetchFeedback()
    },

    async fetchFeedback () {
      this.state.loading = true
      try {
        const { data: payload } = await SStudentFeedback.myFeedback({
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        })

        const result = payload?.data || payload
        this.feedbackItems = result?.items || result?.data || []
        if (result?.pagination) {
          this.pagination = { ...this.pagination, ...result.pagination }
        }
      } catch {
        this.feedbackItems = []
      } finally {
        this.state.loading = false
      }
    },

    async initializeLinkForm () {
      this.linkState.error = ''
      this.state.loadingContext = true

      try {
        if (!this.linkToken) {
          this.linkState.error = 'Invalid feedback link token.'
          return
        }

        const [{ data: contextResponse }, { data: usersResponse }] = await Promise.all([
          SStudentFeedback.getLinkContext(this.linkToken),
          SSharedList.getUsers({ is_active: 1, per_page: 500 })
        ])

        const root = contextResponse?.data || contextResponse
        const context = root?.data || root

        this.linkAppointment = context?.appointment || null
        if (!this.linkAppointment?.id) {
          this.linkState.error = 'This feedback link is invalid or no longer available.'
          return
        }

        this.recipients = usersResponse?.data?.data || usersResponse?.data?.items || []

        this.form = defaultForm()
        this.form.link_token = this.linkToken
        this.form.appointment_id = this.linkAppointment.id
        this.form.recipient_id = context?.recipient_id || this.linkAppointment.user_id || this.recipientOptions[0]?.value || null
      } catch (error) {
        this.linkState.error = error?.response?.data?.message || 'Unable to load feedback form context. Please try again.'
      } finally {
        this.state.loadingContext = false
      }
    },

    async submitFeedback () {
      if (!this.canSubmit) {
        this.swalInvalid('The feedback link is not ready for submission. Please refresh and try again.')
        return
      }

      this.state.submitting = true
      try {
        const payload = {
          appointment_id: this.form.appointment_id,
          recipient_id: this.form.recipient_id,
          link_token: this.form.link_token,
          rating: Number(this.form.rating),
          comments: this.form.comments || null,
          feedback_type: this.form.feedback_type,
          other_type: this.form.feedback_type === 'other' ? this.form.other_type : null
        }

        await SStudentFeedback.submit(payload)
        this.swalSuccess('Feedback submitted successfully.')

        this.form = defaultForm()
        this.linkAppointment = null
        this.linkState.error = ''

        await this.fetchFeedback()

        this.$router.replace({ name: 'student.feedback' })
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'Failed to submit feedback.'
        this.swalInvalid(message)
      } finally {
        this.state.submitting = false
      }
    }
  }
}
</script>
